import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  useTheme,
  IconButton,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../contexts/AuthContext';
import LoginIcon from '@mui/icons-material/Login';
import SecurityIcon from '@mui/icons-material/Security';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
// Capability icons (industry-agnostic)
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
// Industry icons (used in the "trusted across" strip)
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import BioNeuralBackground from './components/NeuralBackground';
import { tokens } from '../../theme';

// Brand mark — vectorized faithfully from logo.png:
//   1) tall outlined shackle (padlock arch) on top
//   2) navy CLOUD body in the original is rendered here as a WHITE cloud
//   3) WHITE heart in the original is rendered here as a TRANSPARENT knockout
//      through the cloud (via an SVG mask), so the dark page background
//      shows through — visually equivalent to the original's white heart on
//      a navy cloud, just inverted for a dark hero background.
//   4) the heartbeat / EKG line sits inside that knockout in white.
//
// Single-color: everything is `currentColor` (white in the header) — no
// per-path hex hardcoding, so this mark can be themed in one place.
const BrandMark = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: 'block', flexShrink: 0 }}
  >
    <defs>
      {/* Mask: white = keep, black = punch through. The heart is punched out
          of the cloud so the dark background shows through, giving the
          original's "heart-inside-cloud" silhouette. */}
      <mask id="dt-heart-mask">
        <rect x="0" y="0" width="64" height="64" fill="white" />
        <path
          d="M32 50
             C 32 50, 18 41, 18 33
             C 18 28, 23 26, 27 29
             C 29 31, 31 33, 32 35
             C 33 33, 35 31, 37 29
             C 41 26, 46 28, 46 33
             C 46 41, 32 50, 32 50 Z"
          fill="black"
        />
      </mask>
    </defs>

    {/* Shackle — tall narrow U at the top of the mark */}
    <path
      d="M23 30 V15
         C 23 10, 27 6, 32 6
         C 37 6, 41 10, 41 15
         V 30"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />

    {/* Cloud body — the lock body, shaped like a fluffy cloud. Mask cuts the
        heart out of it. */}
    <g mask="url(#dt-heart-mask)">
      <path
        d="M 50 56
           L 14 56
           C 8 56, 4 52, 4 46
           C 4 41, 8 37, 13 37
           C 13 30, 19 26, 25 28
           C 27 23, 33 21, 38 24
           C 41 20, 47 21, 50 27
           C 56 27, 60 32, 59 38
           C 62 41, 61 49, 56 50
           C 56 54, 53 56, 50 56
           Z"
        fill="currentColor"
      />
    </g>

    {/* EKG / heartbeat line — drawn AFTER the mask so it sits in the heart
        knockout, visible against the dark page background. */}
    <polyline
      points="19,39 23,39 26,39 28,33 30,46 32,30 34,46 36,33 38,39 41,39 45,39"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ---- Professional SVG hero illustration (federated network) -----------------
const FederatedNetworkSvg = () => (
  <svg
    viewBox="0 0 520 360"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <defs>
      <radialGradient id="hero-core" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.95" />
        <stop offset="60%" stopColor="#0087B4" stopOpacity="0.65" />
        <stop offset="100%" stopColor="#0087B4" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="hero-edge" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
      </linearGradient>
      <filter id="hero-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="4" />
      </filter>
    </defs>

    {/* outer orbital rings */}
    <circle cx="260" cy="180" r="160" fill="none" stroke="rgba(125,211,252,0.18)" strokeWidth="1" strokeDasharray="2 6" />
    <circle cx="260" cy="180" r="120" fill="none" stroke="rgba(125,211,252,0.25)" strokeWidth="1" />
    <circle cx="260" cy="180" r="78" fill="none" stroke="rgba(125,211,252,0.35)" strokeWidth="1.25" />

    {/* edges */}
    {[
      [260, 180, 100, 60],
      [260, 180, 420, 80],
      [260, 180, 80, 280],
      [260, 180, 440, 280],
      [260, 180, 260, 30],
      [260, 180, 260, 330],
    ].map(([x1, y1, x2, y2], i) => (
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#hero-edge)" strokeWidth="1" />
    ))}

    {/* satellite nodes */}
    {[
      [100, 60],
      [420, 80],
      [80, 280],
      [440, 280],
      [260, 30],
      [260, 330],
    ].map(([cx, cy], i) => (
      <g key={`n-${i}`}>
        <circle cx={cx} cy={cy} r="14" fill="rgba(15,23,42,0.6)" stroke="#7dd3fc" strokeWidth="1.25" />
        <circle cx={cx} cy={cy} r="4" fill="#7dd3fc" />
      </g>
    ))}

    {/* central core */}
    <circle cx="260" cy="180" r="60" fill="url(#hero-core)" filter="url(#hero-glow)" />
    <circle cx="260" cy="180" r="34" fill="rgba(15,23,42,0.85)" stroke="#7dd3fc" strokeWidth="1.5" />
    <circle cx="260" cy="180" r="6" fill="#a5f3fc" />

    {/* gridlines under */}
    <g opacity="0.15">
      {[80, 130, 180, 230, 280].map((y) => (
        <line key={y} x1="40" y1={y} x2="480" y2={y} stroke="#7dd3fc" strokeWidth="0.5" strokeDasharray="3 6" />
      ))}
    </g>
  </svg>
);

const Homepage = () => {
  const { login, loading, error } = useAuth();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [langAnchorEl, setLangAnchorEl] = React.useState(null);
  const langOpen = Boolean(langAnchorEl);

  const handleLangClick = (event) => setLangAnchorEl(event.currentTarget);
  const handleLangClose = () => setLangAnchorEl(null);
  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    handleLangClose();
  };

  // Capability-led feature cards. The platform itself is industry-agnostic —
  // each card describes what the product does, not who uses it. Specific
  // verticals are surfaced in the `industries` strip below.
  const features = [
    {
      icon: <HubOutlinedIcon sx={{ fontSize: 20 }} />,
      title: 'Secure data collaboration',
      description:
        'Train across distributed partner sites without moving raw data. Datasets stay on-premise; only model gradients leave each site.',
      cta: 'Explore the network',
    },
    {
      icon: <LayersOutlinedIcon sx={{ fontSize: 20 }} />,
      title: 'Production-ready models',
      description:
        'Deploy proven architectures for tabular, imaging, and segmentation workloads, or bring your own. Built for any domain, not just one.',
      cta: 'Browse models',
    },
    {
      icon: <InsightsOutlinedIcon sx={{ fontSize: 20 }} />,
      title: 'Compliance-first insights',
      description:
        'Audit-ready runs with per-site validation, encrypted aggregation, and full lineage. Ship results your privacy & legal teams will approve.',
      cta: 'See how it works',
    },
  ];

  // Industries served — shown as a horizontal trust strip beneath the hero.
  // Order is just visual rhythm; no single sector is positioned as primary.
  const industries = [
    { icon: <LocalHospitalOutlinedIcon sx={{ fontSize: 14 }} />,           label: 'Healthcare & life sciences' },
    { icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 14 }} />,          label: 'Financial services' },
    { icon: <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 14 }} />,  label: 'Manufacturing' },
    { icon: <BoltOutlinedIcon sx={{ fontSize: 14 }} />,                    label: 'Energy & utilities' },
    { icon: <StorefrontOutlinedIcon sx={{ fontSize: 14 }} />,              label: 'Retail & e-commerce' },
    { icon: <SatelliteAltOutlinedIcon sx={{ fontSize: 14 }} />,            label: 'Telecom' },
    { icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 14 }} />,    label: 'Public sector' },
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(160deg, #0b1629 0%, ${colors.primary[700]} 55%, #0b1629 100%)`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: '#e2e8f0',
      }}
    >
      {/* Subtle dot grid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(circle at center, black 35%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(circle at center, black 35%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      <BioNeuralBackground transparent />

      {/* Header */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, md: 5 },
          py: 2.5,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            color: '#ffffff',
          }}
          aria-label="FederateX"
        >
          <img src="/federatex-mark.svg" alt="FederateX" width={30} height={30} style={{ borderRadius: 6 }} />
          <Typography
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              fontSize: { xs: '0.875rem', md: '0.9375rem' },
              letterSpacing: '0.04em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            FEDERATE<Box component="span" sx={{ color: '#36CE7B' }}>X</Box>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleLangClick}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': { color: '#7dd3fc', backgroundColor: 'rgba(125, 211, 252, 0.08)' },
            }}
          >
            <LanguageIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Menu
            anchorEl={langAnchorEl}
            open={langOpen}
            onClose={handleLangClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  backgroundColor: '#0f172a',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 1.5,
                  minWidth: 160,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
                  // Explicitly style children so the menu items aren't
                  // inheriting the global "light theme" dark text color.
                  '& .MuiMenuItem-root': {
                    color: '#e2e8f0',
                    fontSize: '0.8125rem',
                    py: 0.9,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(125, 211, 252, 0.10)',
                      color: '#ffffff',
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: 'rgba(125, 211, 252, 0.16)',
                      color: '#ffffff',
                    },
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => handleLanguageChange('en')}
              selected={i18n.language === 'en'}
              sx={{ gap: 1.25 }}
            >
              <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1, width: 22, textAlign: 'center' }}>🇬🇧</Box>
              <Box component="span" sx={{ flex: 1 }}>English</Box>
              {i18n.language === 'en' && <CheckIcon sx={{ fontSize: 16, color: '#7dd3fc' }} />}
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange('fr')}
              selected={i18n.language === 'fr'}
              sx={{ gap: 1.25 }}
            >
              <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1, width: 22, textAlign: 'center' }}>🇫🇷</Box>
              <Box component="span" sx={{ flex: 1 }}>Français</Box>
              {i18n.language === 'fr' && <CheckIcon sx={{ fontSize: 16, color: '#7dd3fc' }} />}
            </MenuItem>
          </Menu>

          <Button
            variant="outlined"
            startIcon={loading ? <AccountCircleIcon /> : <LoginIcon sx={{ fontSize: 16 }} />}
            onClick={login}
            disabled={loading}
            sx={{
              px: 2,
              py: 0.6,
              height: 34,
              fontSize: '0.8125rem',
              fontWeight: 600,
              border: '1px solid rgba(125, 211, 252, 0.55)',
              borderRadius: 1.25,
              color: '#7dd3fc',
              textTransform: 'none',
              backgroundColor: 'rgba(125, 211, 252, 0.06)',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#ffffff',
                backgroundColor: '#0087B4',
                borderColor: '#0087B4',
              },
            }}
          >
            {loading ? t('menu.connecting') : t('menu.signIn')}
          </Button>
        </Box>
      </Box>

      {/* Main */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          pt: { xs: 14, md: 12 },
          pb: 6,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <motion.div variants={container} initial="hidden" animate="visible" style={{ width: '100%' }}>
          <Grid container spacing={6} alignItems="center" sx={{ mb: 6 }}>
            {/* LEFT — copy */}
            <Grid item xs={12} md={7}>
              <motion.div variants={item}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 6,
                    backgroundColor: 'rgba(125, 211, 252, 0.08)',
                    border: '1px solid rgba(125, 211, 252, 0.25)',
                    color: '#7dd3fc',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: 2.5,
                  }}
                >
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 13 }} />
                  Federated learning platform
                </Box>
              </motion.div>

              <motion.div variants={item}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#ffffff',
                    fontSize: { xs: '2.25rem', md: '2.75rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.025em',
                    mb: 1.5,
                  }}
                >
                  Train AI{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #7dd3fc 0%, #0087B4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    across organizations
                  </Box>{' '}
                  without moving the data.
                </Typography>
              </motion.div>

              <motion.div variants={item}>
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: { xs: '0.9375rem', md: '1rem' },
                    lineHeight: 1.65,
                    mb: 3,
                    maxWidth: 580,
                  }}
                >
                  A federated learning platform for any industry where data is too sensitive,
                  too regulated, or too distributed to centralize, from healthcare and finance
                  to manufacturing, energy, and beyond.
                </Typography>
              </motion.div>

              {error && (
                <motion.div variants={item}>
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      maxWidth: 480,
                      backgroundColor: 'rgba(220, 38, 38, 0.12)',
                      color: '#fca5a5',
                      border: '1px solid rgba(220, 38, 38, 0.35)',
                      backdropFilter: 'blur(8px)',
                      fontSize: '0.8125rem',
                    }}
                  >
                    Authentication Error: {error}
                  </Alert>
                </motion.div>
              )}

              <motion.div variants={item}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    onClick={login}
                    disabled={loading}
                    startIcon={loading ? <AccountCircleIcon /> : <LoginIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      px: 2.25,
                      py: 0.9,
                      backgroundColor: '#0087B4',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      borderRadius: 1.25,
                      textTransform: 'none',
                      boxShadow: '0 6px 24px rgba(0,135,180,0.30)',
                      '&:hover': { backgroundColor: '#00769d', boxShadow: '0 6px 32px rgba(0,135,180,0.45)' },
                    }}
                  >
                    {loading ? t('menu.connecting') : t('menu.signIn')}
                  </Button>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#7dd3fc', fontSize: '0.75rem', fontWeight: 500 }}>
                    <SecurityIcon sx={{ fontSize: 14 }} />
                    SOC 2 · GDPR · HIPAA · ISO 27001 aligned
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* RIGHT — SVG hero */}
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.div variants={item}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 480,
                    mx: 'auto',
                    p: 2,
                  }}
                >
                  <FederatedNetworkSvg />
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Industries strip — communicates breadth without hierarchy */}
          <motion.div variants={item}>
            <Box
              sx={{
                position: 'relative',
                mb: 4,
                py: 1.75,
                px: 2,
                borderRadius: 1.5,
                border: '1px solid rgba(255,255,255,0.06)',
                backgroundColor: 'rgba(255,255,255,0.02)',
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                  pr: 1.5,
                  borderRight: { md: '1px solid rgba(255,255,255,0.08)' },
                }}
              >
                Trusted across
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: 1 }}>
                {industries.map((ind) => (
                  <Box
                    key={ind.label}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1,
                      py: 0.4,
                      borderRadius: 999,
                      bgcolor: 'rgba(125, 211, 252, 0.06)',
                      border: '1px solid rgba(125, 211, 252, 0.18)',
                      color: '#cbd5e1',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(125, 211, 252, 0.10)',
                        borderColor: 'rgba(125, 211, 252, 0.35)',
                        color: '#ffffff',
                      },
                    }}
                  >
                    <Box component="span" sx={{ color: '#7dd3fc', display: 'inline-flex', alignItems: 'center' }}>
                      {ind.icon}
                    </Box>
                    {ind.label}
                  </Box>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Features */}
          <Grid container spacing={2.5} sx={{ mt: 1 }}>
            {features.map((f, i) => (
              <Grid item xs={12} md={4} key={i}>
                <motion.div variants={item} style={{ height: '100%' }}>
                  <Box
                    className="group"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.025)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.045)',
                        borderColor: 'rgba(125, 211, 252, 0.35)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: 14,
                        right: 16,
                        fontFamily: 'var(--fx-font-mono)',
                        fontVariantNumeric: 'tabular-nums',
                        color: 'rgba(255,255,255,0.20)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                      }}
                    >
                      {`0${i + 1}`}
                    </Typography>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1.25,
                        backgroundColor: 'rgba(0, 135, 180, 0.12)',
                        border: '1px solid rgba(0, 135, 180, 0.30)',
                        color: '#7dd3fc',
                        mb: 1.5,
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#ffffff', mb: 0.5, fontSize: '0.9375rem' }}>
                      {f.title}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', lineHeight: 1.5, fontSize: '0.8125rem', mb: 1.5, flex: 1 }}>
                      {f.description}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        marginTop: 'auto',
                        color: '#7dd3fc',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        opacity: 0.9,
                      }}
                    >
                      {f.cta}
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          transition: 'transform 0.2s ease',
                          '.group:hover &': { transform: 'translateX(3px)' },
                        }}
                      >
                        →
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: 'rgba(11, 22, 41, 0.85)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          py: 2,
          width: '100%',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#7dd3fc' }}>
              <SecurityIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                Enterprise-grade security
              </Typography>
            </Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
              Powered by Keycloak · Aligned with global privacy & data-protection standards
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Homepage;
